import { ReactNode, createContext, useContext } from "react";
import { useCalculatorTheme } from "@/contexts/CalculatorThemeContext";

interface CalculatorContentContextType {
  isDark: boolean;
  classes: {
    text: string;
    textSecondary: string;
    textMuted: string;
    textAccent: string;
    card: string;
    cardHover: string;
    input: string;
    border: string;
    buttonOutline: string;
  };
}

const CalculatorContentContext = createContext<CalculatorContentContextType | null>(null);

export const useCalculatorContent = () => {
  const ctx = useContext(CalculatorContentContext);
  if (!ctx) {
    // Return light mode defaults if not in context
    return {
      isDark: false,
      classes: {
        text: "text-slate-900",
        textSecondary: "text-slate-600",
        textMuted: "text-slate-500",
        textAccent: "text-navy",
        card: "bg-white/80 border-slate-200",
        cardHover: "hover:bg-slate-50",
        input: "bg-white/80 border-slate-200 text-slate-900 placeholder:text-slate-400",
        border: "border-slate-200",
        buttonOutline: "bg-white/80 border-slate-200 text-slate-700 hover:bg-slate-50",
      },
    };
  }
  return ctx;
};

interface CalculatorContentWrapperProps {
  children: ReactNode;
}

/**
 * Wrapper component that provides theme-aware classes to calculator content.
 * Place this inside CalculatorLayout to give child components access to theme classes.
 */
export const CalculatorContentWrapper = ({ children }: CalculatorContentWrapperProps) => {
  const { isDark } = useCalculatorTheme();

  const classes = isDark
    ? {
        text: "text-white",
        textSecondary: "text-white/70",
        textMuted: "text-white/50",
        textAccent: "text-amber-400",
        card: "bg-white/5 border-white/10",
        cardHover: "hover:bg-white/10",
        input: "bg-white/5 border-white/20 text-white placeholder:text-white/30",
        border: "border-white/10",
        buttonOutline: "bg-white/5 border-white/20 text-white hover:bg-white/10",
      }
    : {
        text: "text-slate-900",
        textSecondary: "text-slate-600",
        textMuted: "text-slate-500",
        textAccent: "text-navy",
        card: "bg-white/80 border-slate-200",
        cardHover: "hover:bg-slate-50",
        input: "bg-white/80 border-slate-200 text-slate-900 placeholder:text-slate-400",
        border: "border-slate-200",
        buttonOutline: "bg-white/80 border-slate-200 text-slate-700 hover:bg-slate-50",
      };

  return (
    <CalculatorContentContext.Provider value={{ isDark, classes }}>
      {children}
    </CalculatorContentContext.Provider>
  );
};

export default CalculatorContentWrapper;